import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { PencilIcon, useHapticFeedback } from '@blade/ui-kit'
import { ThemePreview } from '@root/entities/theme/ui/theme-preview'
import { settingScreenAction } from '@root/features/settings/settings-screen/atoms'
import { useTranslation } from '@root/shared/localization'
import {
  availableThemes,
  currentCustomUiThemeAtom,
  currentThemeIdAtom,
  ThemeId,
  type Theme,
} from '@root/shared/theme'
import { mediaMaxMobile } from '@root/shared/ui-kit'

import type { TransKey } from '@root/shared/localization'

const ID_TITLE_MAP: Record<ThemeId, TransKey> = {
  [ThemeId.Deluge]: 'theme.deluge.name',
  [ThemeId.DelugeLight]: 'theme.delugeLight.name',
  [ThemeId.Test]: 'theme.test.name',
  [ThemeId.Photgone]: 'theme.photgone.name',
  [ThemeId.Monokai]: 'theme.monokai.name',
}

type ThemeItem = Theme | 'custom'

const buildThemeItems = (): ReadonlyArray<ThemeItem> => {
  const [first, ...rest] = availableThemes

  return first ? [first, 'custom', ...rest] : ['custom', ...rest]
}

export const SettingsAppSettingsTheme = () => {
  const { t } = useTranslation()

  const haptics = useHapticFeedback()
  const [activeThemeId, setActiveThemeId] = useAtom(currentThemeIdAtom)
  const [customUiTheme] = useAtom(currentCustomUiThemeAtom)
  const setSettingScreen = useAction(settingScreenAction)
  const themeItems = buildThemeItems()

  const handleSelectTheme = (themeId: ThemeId | 'custom') => {
    haptics.impactOccurred('light')
    setActiveThemeId(themeId)
  }

  const handleEditCustomTheme = () => {
    haptics.impactOccurred('light')
    setSettingScreen('app-settings-custom-theme')
  }

  return (
    <Root>
      <ContentWrapper>
        {themeItems.map((item) => {
          const isCustom = item === 'custom'
          const themeId = isCustom ? 'custom' : item.id
          const isActive = themeId === activeThemeId
          const previewTheme = isCustom ? customUiTheme : item.uiTheme
          const title = isCustom
            ? t('theme.custom.name')
            : t(ID_TITLE_MAP[item.id])

          return (
            <Item key={themeId} $isActive={isActive}>
              <SelectButton
                type="button"
                onClick={() => handleSelectTheme(themeId)}
              >
                <ItemContent $isActive={isActive}>
                  <ThemePreviewContainer>
                    <ThemePreview dashed={isCustom} theme={previewTheme} />
                  </ThemePreviewContainer>
                </ItemContent>
              </SelectButton>
              <TitleRow>
                <Title>{title}</Title>
                {isCustom ? (
                  <PencilButton type="button" onClick={handleEditCustomTheme}>
                    <PencilIcon size={14} />
                  </PencilButton>
                ) : undefined}
              </TitleRow>
            </Item>
          )
        })}
      </ContentWrapper>
    </Root>
  )
}

const Root = styled.div`
  padding: 12px;
`
const ThemePreviewContainer = styled.div`
  margin-bottom: -24px;
`
const ContentWrapper = styled.div`
  display: grid;
  border-radius: 10px;

  gap: 12px;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: max-content;
  background: transparent;
  ${mediaMaxMobile(css`
    padding: 12px;
    border-radius: 10px;
    background: var(--background-2);
  `)}
`

const ItemContent = styled.div<{ $isActive: boolean }>`
  background: var(--appearance-1);
  border-radius: 10px;
  outline: 1px solid transparent;
  outline-color: ${(x) => (x.$isActive ? 'var(--primary-9)' : 'none')};
  /* padding: 12px; */
  overflow: hidden;
`

const SelectButton = styled.button`
  all: unset;
  cursor: pointer;
`

const Item = styled.div<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-10)')};

  &:hover {
    color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-11)')};
  }

  ${SelectButton}:hover ${ItemContent} {
    outline-color: ${(x) =>
      x.$isActive ? 'var(--primary-9)' : 'var(--primary-6)'};
  }
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`

const Title = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: 500;
`

const PencilButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-10);

  &:hover {
    color: var(--text-12);
  }
`
